import {
    Box,
    Drawer,
    Link,
    ListSubheader,
    Stack,
    Typography,
} from '@mui/material';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import {
    ApartmentOutlined,
    EventAvailable,
    EventRepeat,
    GridViewOutlined,
    ManageAccountsOutlined,
    PersonOutline,
    TaskAltOutlined,
    TimerOutlined,
    WorkHistoryOutlined,
} from '@mui/icons-material';

import scedasLogo from '../../assets/scedas-timekeeper-logo.svg';
import MenuItem from './MenuItem';
import { useContext } from 'react';
import { Context } from '../../context/context';
import i18n from '../../i18n';

const lists = [
    {
        roles: ['Mitarbeiter', 'Maintainer', 'Reporter', 'Administrator', 'HR-Manager'],
        items: [
            {
                labelKey: 'startseite',
                defaultLabel: 'Startseite',
                icon: GridViewOutlined,
                to: '/',
                roles: ['Mitarbeiter', 'Maintainer', 'Reporter', 'Administrator', 'HR-Manager'],
            }, {
                labelKey: 'zeiterfassung',
                defaultLabel: 'Zeiterfassung',
                icon: TimerOutlined,
                to: '/timekeeper',
                roles: ['Mitarbeiter', 'Maintainer', 'Reporter', 'Administrator', 'HR-Manager'],
            }, {
                labelKey: 'monatsfreigabe',
                defaultLabel: 'Monatsfreigabe',
                icon: EventAvailable,
                to: '/submits',
                roles: ['Mitarbeiter', 'Maintainer', 'Reporter', 'Administrator', 'HR-Manager']
            },
        ],
    }, {
        labelKey: 'nachweise',
        defaultLabel: 'Nachweise',
        roles: ['Reporter'],
        items: [
            {
                labelKey: 'statusübersicht',
                defaultLabel: 'Statusübersicht',
                icon: TaskAltOutlined,
                to: '/status-overview',
                roles: ['Reporter']
            }, {
                labelKey: 'export',
                defaultLabel: 'Export',
                icon: WorkHistoryOutlined,
                to: '/work-records',
                roles: ['Reporter']
            },
        ],
    }, {
        labelKey: 'verwaltung',
        defaultLabel: 'Verwaltung',
        roles: ['Administrator', 'HR-Manager'],
        items: [
            {
                labelKey: 'nutzerzugänge',
                defaultLabel: 'Nutzerzugänge',
                icon: ManageAccountsOutlined,
                to: '/user-access',
                roles: ['Administrator']
            }, {
                labelKey: 'stammdaten',
                defaultLabel: 'Stammdaten',
                icon: EventRepeat,
                to: '/employee-base-data',
                roles: ['HR-Manager']
            },
        ],
    }, {
        labelKey: 'einstellungen',
        defaultLabel: 'Einstellungen',
        roles: ['Mitarbeiter', 'Maintainer', 'Reporter', 'Administrator', 'HR-Manager'],
        items: [
            {
                labelKey: 'persönliches',
                defaultLabel: 'Persönliches',
                icon: PersonOutline,
                to: '/user-settings',
                roles: ['Mitarbeiter', 'Maintainer', 'Reporter', 'Administrator', 'HR-Manager']
            },
            {
                labelKey: 'institutskonfiguration',
                defaultLabel: 'Institutskonfiguration',
                icon: ApartmentOutlined,
                to: '/institute-configuration',
                roles: ['Maintainer']
            },
        ],
    },
];

function Sidebar({ drawerWidth }) {
    const { t } = useTranslation();
    const [{ user }, dispatch] = useContext(Context)

    const getTranslatedLists = () => lists.map(section => ({
        ...section,
        label: section.labelKey ? t(section.labelKey, { ns: 'sideBar', defaultValue: section.defaultLabel }) : undefined,
        items: section.items.map(item => ({
            ...item,
            label: t(item.labelKey, { ns: 'sideBar', defaultValue: item.defaultLabel }),
        })),
    }));

    const translatedLists = getTranslatedLists();

    const { pathname } = useLocation();
    return (
        <Drawer
            sx={{
                width: drawerWidth,
                '& .MuiDrawer-paper': {
                    width: drawerWidth,
                    boxSizing: 'border-box',
                },
            }}
            variant='persistent'
            open
        >
            <Box component="img" src={scedasLogo} sx={{ height: 64 }} />
            <Box
                sx={{ py: 3, px: 2, flexGrow: 1 }}
            >
                {translatedLists.map((list, i) => (
                    user.roles.some(r => list.roles.find(role => role === r.name)) &&
                    <Box sx={{ mb: 3 }} key={i}>
                        {list.label && <ListSubheader sx={{ textAlign: 'left', typography: 'subtitle2' }}>{list.label}</ListSubheader>}
                        <Stack
                            component="ul"
                            sx={{ p: 0, mt: 1 }}
                            spacing={0.5}
                        >
                            {list.items.map((item) => {
                                const active = item.to ? (pathname === item.to) : false;
                                const isAuthorized = user.roles.some(r => item.roles.find(role => role === r.name))
                                return (
                                    isAuthorized &&
                                    <MenuItem to={item.to} label={item.label} Icon={item.icon} active={active} key={item.label} />
                                );
                            })}
                        </Stack>
                    </Box>
                ))}
            </Box>
            <Box
                sx={{
                    py: 2, px: 4,
                    color: 'grey.400',
                }}
            >
                <Stack>
                    <Typography variant="subtitle2">
                        <Link href={`https://dsi.informationssicherheit.fraunhofer.de/${i18n.language === 'de' ? 'de' : 'en'}/dsi/scedas-timekeeper.fraunhofer.de`}>
                            <Trans i18nKey='datenschutzerklärung' ns='sideBar'>Datenschutzerklärung</Trans>
                        </Link>
                    </Typography>
                    <Typography variant="subtitle2">
                        <Link href={`https://dsi.informationssicherheit.fraunhofer.de/${i18n.language === 'de' ? 'de' : 'en'}/impressum/scedas-timekeeper.fraunhofer.de`}>
                            <Trans i18nKey='impressum' ns='sideBar'>Impressum</Trans>
                        </Link>
                    </Typography>
                    <Typography variant="subtitle2">
                        <Trans i18nKey='version' ns='sideBar'>Version</Trans>
                        {`: ${process.env.REACT_APP_VERSION}`}
                    </Typography>
                </Stack>
                <Typography
                    variant="subtitle2"
                    sx={{
                        fontWeight: 'light',
                        fontStyle: 'italic',
                        fontSize: 12,
                    }}
                >
                    <Trans i18nKey='bug-reports' ns='sideBar'>Technische Fehlermeldungen bitte </Trans>
                    <Link href="mailto:scedas-support@cml.fraunhofer.de" underline="always">{i18n.language === 'de' ? 'hier' : 'here'}</Link>
                </Typography>
            </Box>
        </Drawer>
    );
};

export default Sidebar;
